/**
 * ToolGovernance.tsx — the Tool Governance panel, opened in place over AdminHome (and still routable
 * at /admin/tools via the thin wrapper below). Two owner/moderator surfaces on one panel:
 *
 *  1. The admin-side TRIAGE index (TD-F23): every currently Broken or ownerless LIVE tool, each
 *     linking to its own detail page — that is where the per-tool actions (set health, edit
 *     category/tags, withdraw, merge, reassign owner) live, via ToolDetail.tsx's OwnerActionsPanel.
 *     This panel does not re-implement those; it is the "what needs attention" index PC-F17 describes.
 *
 *  2. CATEGORY GOVERNANCE (CAT-F06/F07/F08, PlatformOwner only): add, rename and retire the primary
 *     categories WITHOUT a developer or a deploy (CAT-NFR04). Retiring a category immediately drops
 *     it from every upload dropdown and catalogue filter (the ref-data read already hides retired
 *     rows), and a category that still has tools under it cannot be retired (CAT-F08) — the tool
 *     count is shown so the owner knows to re-home first. A category is never deleted, only retired,
 *     so the audit trail and any tool that still FKs it stay intact.
 *
 * OVERLAY BEHAVIOUR. The body is `ToolGovernancePanel`, mounted by AdminHome inside the shared Kombi
 * admin drawer (`KombiAdminOverlay` in kombi/admin/chrome.tsx). It fetches its own data on open via
 * `useOverlayData(toolGovernanceLoader)` (a pending line while it resolves), and its mutations
 * `reload()` that fetch in place rather than closing the panel. `ToolGovernance` stays as a thin route
 * wrapper so /admin/tools keeps resolving to the same panel, closing back to /admin.
 */
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { apiGet, apiPatch, apiPost, type AdminCategory, type FlaggedTool } from './api';
import { useOverlayData, type ReturnFocusRef } from './overlay';
import {
  EmptyState,
  ErrorLine,
  KombiAdminOverlay,
  Notice,
  NotPermitted,
  PrimaryAction,
  ProvisionalNote,
  QuietAction,
  SectionHeader,
  Spinner,
} from '../kombi/admin/chrome';

export type ToolGovernanceLoaderData =
  | { state: 'signedOut' }
  | { state: 'forbidden' }
  | { state: 'ok'; tools: FlaggedTool[]; categories: AdminCategory[] };

export async function toolGovernanceLoader(): Promise<ToolGovernanceLoaderData> {
  const [flaggedRes, categoriesRes] = await Promise.all([
    apiGet<{ tools: FlaggedTool[] }>('/api/admin/tools/flagged'),
    apiGet<{ categories: AdminCategory[] }>('/api/admin/tools/categories'),
  ]);
  if (flaggedRes.kind === 'unauthorized' || categoriesRes.kind === 'unauthorized')
    return { state: 'signedOut' };
  if (flaggedRes.kind === 'error') {
    if (flaggedRes.status === 403) return { state: 'forbidden' };
    throw new Error(`GET /api/admin/tools/flagged failed (${flaggedRes.status})`);
  }
  if (categoriesRes.kind === 'error') {
    if (categoriesRes.status === 403) return { state: 'forbidden' };
    throw new Error(`GET /api/admin/tools/categories failed (${categoriesRes.status})`);
  }
  return { state: 'ok', tools: flaggedRes.body.tools, categories: categoriesRes.body.categories };
}

/** Turn an error code from the category endpoints into a sentence the owner can act on. */
function categoryErrorMessage(code: string | null, toolCount?: number): string {
  switch (code) {
    case 'duplicate_label':
      return 'A category with that name already exists.';
    case 'forbidden_label':
      return 'That name is a Microsoft product — those belong as tags, not categories.';
    case 'not_empty':
      return `This category still has ${toolCount ?? 'some'} tool${
        toolCount === 1 ? '' : 's'
      } under it. Re-home ${toolCount === 1 ? 'it' : 'them'} to another category first.`;
    case 'already_in_state':
      return 'That category is already in that state.';
    case 'category_not_found':
      return 'That category no longer exists.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

/** The add-a-category form. */
function AddCategoryForm({ onAdded }: { onAdded: () => void }) {
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add() {
    const trimmed = label.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiPost<{ ok: true }>('/api/admin/tools/categories', { label: trimmed });
      if (res.kind === 'ok') {
        setLabel('');
        onAdded();
        return;
      }
      setError(
        res.kind === 'error' ? categoryErrorMessage(res.code) : 'You are no longer signed in.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="kombi-dialog-stack">
      {/* Cap at 60 on the way into state, so the value is capped however the text arrives. */}
      <label className="kombi-field">
        <span>Name</span>
        <input
          id="cat-new-label"
          name="cat-new-label"
          onChange={(e) => setLabel(e.target.value.slice(0, 60))}
          placeholder="e.g. Prototypes"
          type="text"
          value={label}
        />
      </label>
      <ErrorLine>{error}</ErrorLine>
      <PrimaryAction onClick={() => void add()} disabled={busy || label.trim().length === 0}>
        {busy ? 'Adding…' : 'Add category'}
      </PrimaryAction>
    </div>
  );
}

/** One category row: shows its tool count and live/retired state, with rename and retire/restore. */
function CategoryRow({ category, onChanged }: { category: AdminCategory; onChanged: () => void }) {
  const [renaming, setRenaming] = useState(false);
  const [draftLabel, setDraftLabel] = useState(category.label);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const retired = category.retiredAt !== null;

  async function patch(body: { label?: string; retired?: boolean }) {
    setBusy(true);
    setError(null);
    try {
      const res = await apiPatch<{ ok: true }>(`/api/admin/tools/categories/${category.id}`, body);
      if (res.kind === 'ok') {
        setRenaming(false);
        onChanged();
        return;
      }
      // `not_empty` carries its tool count under `detail` (the only field, besides `code`, the
      // apiPatch normaliser forwards from the error body).
      const toolCount =
        res.kind === 'error'
          ? (res.detail as { toolCount?: number } | undefined)?.toolCount
          : undefined;
      setError(
        res.kind === 'error'
          ? categoryErrorMessage(res.code, toolCount)
          : 'You are no longer signed in.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <li>
      {renaming ? (
        // Cap at 60 on the way into state, matching the add form.
        <label className="kombi-field">
          <span>Name</span>
          <input
            id={`cat-rename-${category.id}`}
            name={`cat-rename-${category.id}`}
            onChange={(e) => setDraftLabel(e.target.value.slice(0, 60))}
            type="text"
            value={draftLabel}
          />
        </label>
      ) : (
        <>
          <span>{category.label}</span>
          <p>
            {retired ? 'Retired' : 'Live'} · {category.toolCount} tool
            {category.toolCount === 1 ? '' : 's'}
          </p>
        </>
      )}
      <ErrorLine>{error}</ErrorLine>
      {renaming ? (
        <div className="kombi-admin-row-actions">
          <PrimaryAction
            onClick={() => void patch({ label: draftLabel.trim() })}
            disabled={
              busy || draftLabel.trim().length === 0 || draftLabel.trim() === category.label
            }
          >
            {busy ? 'Saving…' : 'Save'}
          </PrimaryAction>
          <QuietAction
            onClick={() => {
              setRenaming(false);
              setDraftLabel(category.label);
              setError(null);
            }}
            disabled={busy}
          >
            Cancel
          </QuietAction>
        </div>
      ) : (
        <div className="kombi-admin-row-actions">
          <QuietAction onClick={() => setRenaming(true)} disabled={busy}>
            Rename
          </QuietAction>
          {retired ? (
            <QuietAction onClick={() => void patch({ retired: false })} disabled={busy}>
              {busy ? 'Restoring…' : 'Restore'}
            </QuietAction>
          ) : (
            <QuietAction onClick={() => void patch({ retired: true })} disabled={busy}>
              {busy ? 'Retiring…' : 'Retire'}
            </QuietAction>
          )}
        </div>
      )}
    </li>
  );
}

/** The overlay body: fetches on open, then renders the two governance surfaces (or a load/error state). */
export function ToolGovernancePanel({
  onClose,
}: {
  onClose: () => void;
  returnFocusRef: ReturnFocusRef;
}) {
  const { data, loading, error, reload } = useOverlayData(toolGovernanceLoader);

  return (
    <KombiAdminOverlay title="Tool Governance" onClose={onClose}>
      {loading ? (
        <Spinner label="Loading tool governance…" />
      ) : error || !data || data.state === 'signedOut' ? (
        <ErrorLine>You are no longer signed in.</ErrorLine>
      ) : data.state === 'forbidden' ? (
        <NotPermitted what="Tool Governance" />
      ) : (
        <>
          <ProvisionalNote />

          <SectionHeader>Categories</SectionHeader>
          <Notice>
            The primary categories a tool can be filed under. Adding one makes it choosable in the
            Share wizard straight away; retiring one removes it from every dropdown and filter. A
            category with tools still under it cannot be retired until those tools are re-homed
            (CAT-F06/F08).
          </Notice>
          <AddCategoryForm onAdded={reload} />
          {data.categories.length === 0 ? (
            <EmptyState>No categories yet.</EmptyState>
          ) : (
            <ul className="kombi-admin-rows">
              {data.categories.map((c) => (
                <CategoryRow key={c.id} category={c} onChanged={reload} />
              ))}
            </ul>
          )}

          <SectionHeader>Flagged tools</SectionHeader>
          <Notice>
            Every live tool currently Broken or needing an owner. Open a tool to act on it — set
            health, edit category/tags, withdraw, merge a duplicate or reassign an ownerless tool —
            from its own detail page (TD-F23).
          </Notice>
          {data.tools.length === 0 ? (
            <EmptyState>Nothing is currently flagged.</EmptyState>
          ) : (
            <ul className="kombi-admin-rows">
              {data.tools.map((t) => (
                <li key={t.toolId}>
                  <a href={`/tools/${t.toolId}`}>{t.name}</a>
                  <p>
                    {t.healthState !== 'Healthy' && `Health: ${t.healthState}`}
                    {t.healthState !== 'Healthy' && t.ownerlessFlag && ' · '}
                    {t.ownerlessFlag && 'Ownerless'}
                    {' · '}
                    Builder: {t.builderName} · {t.publicationStatus}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </KombiAdminOverlay>
  );
}

/** Thin route wrapper: /admin/tools mounts the panel, closing back to /admin. */
export function ToolGovernance() {
  const navigate = useNavigate();
  const ref = useRef<HTMLElement | null>(null);
  return <ToolGovernancePanel onClose={() => navigate('/admin')} returnFocusRef={ref} />;
}
